import React, { useCallback } from "react";
import {
  Grid,
  GridCell,
  Pager,
  PagerItem,
  Select,
  SelectOption,
} from "@flixbus/honeycomb-react";
import {
  Icon,
  IconArrowLeft,
  IconArrowRight,
} from "@flixbus/honeycomb-icons-react";
import type { CalculationPaginationProps } from "./CalculationInputList.types";
import { defaultPageSize } from "./CalculationInputList.constants";
import styles from "./CalculationInputList.module.scss";

type PagerWindowItem =
  | { kind: "page"; page: number }
  | { kind: "ellipsis"; key: string };

const buildPagerWindow = (
  displayPage: number,
  displayTotalPages: number,
): PagerWindowItem[] => {
  if (displayTotalPages <= 5) {
    return Array.from({ length: displayTotalPages }, (_, index) => ({
      kind: "page",
      page: index + 1,
    }));
  }

  const pages = new Set<number>([1, displayTotalPages]);
  const firstNeighbor = Math.max(displayPage - 1, 2);
  const lastNeighbor = Math.min(displayPage + 1, displayTotalPages - 1);

  for (let page = firstNeighbor; page <= lastNeighbor; page += 1) {
    pages.add(page);
  }

  const sortedPages = Array.from(pages).sort((a, b) => a - b);
  const items: PagerWindowItem[] = [];
  let previousPage = 0;

  sortedPages.forEach((page) => {
    if (previousPage > 0 && page - previousPage > 1) {
      items.push({ kind: "ellipsis", key: `${previousPage}-${page}` });
    }
    items.push({ kind: "page", page });
    previousPage = page;
  });

  return items;
};

const getRangeSummary = ({
  displayPage,
  pageSize,
  totalElements,
}: CalculationPaginationProps): string => {
  const firstVisible = Math.min(
    Math.max((displayPage - 1) * pageSize + 1, 1),
    totalElements,
  );
  const lastVisible = Math.min(displayPage * pageSize, totalElements);

  return `${firstVisible}-${lastVisible} of ${totalElements}`;
};

interface Props {
  pagination: CalculationPaginationProps;
}

const CalculationInputListPaginationControls: React.FC<Props> = ({
  pagination,
}) => {
  const handlePageSizeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      pagination.onPageSizeChange(Number(event.target.value));
    },
    [pagination],
  );
  const handlePageSelect = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const page = Number(event.currentTarget.dataset.page);
      if (!Number.isFinite(page)) return;

      pagination.onPageSelect(page - 1);
    },
    [pagination],
  );
  const hasMultiplePages = pagination.displayTotalPages > 1;
  const showPageSizeSelect = pagination.totalElements > defaultPageSize;
  const showRangeSummary = showPageSizeSelect;

  if (!hasMultiplePages && !showRangeSummary && !showPageSizeSelect) {
    return null;
  }

  return (
    <div
      className={styles.paginationControls}
      aria-label="Calculation list controls"
    >
      <Grid align="center" extraClasses={styles.paginationGrid}>
        <GridCell
          colSpan={{ default: 12, md: 3 }}
          extraClasses={styles.paginationSummaryColumn}
        >
          {showRangeSummary && (
            <span className={styles.paginationSummary}>
              {getRangeSummary(pagination)}
            </span>
          )}
        </GridCell>
        <GridCell
          colSpan={{ default: 12, md: 6 }}
          extraClasses={styles.paginationPagerColumn}
        >
          {hasMultiplePages && (
            <Pager Elem="button" aria-label="Calculation list pagination">
              <PagerItem
                aria-label="Previous page"
                disabled={!pagination.canGoPrevious}
                onClick={pagination.onPreviousPage}
              >
                <Icon InlineIcon={IconArrowLeft} aria-hidden="true" />
              </PagerItem>
              {buildPagerWindow(
                pagination.displayPage,
                pagination.displayTotalPages,
              ).map((item) => {
                if (item.kind === "ellipsis") {
                  return (
                    <PagerItem key={item.key} disabled aria-label="More pages">
                      ...
                    </PagerItem>
                  );
                }

                return (
                  <PagerItem
                    key={item.page}
                    active={item.page === pagination.displayPage}
                    disabled={pagination.navigationDisabled}
                    aria-label={`Page ${item.page}`}
                    data-page={item.page}
                    onClick={handlePageSelect}
                  >
                    {item.page}
                  </PagerItem>
                );
              })}
              <PagerItem
                aria-label="Next page"
                disabled={!pagination.canGoNext}
                onClick={pagination.onNextPage}
              >
                <Icon InlineIcon={IconArrowRight} aria-hidden="true" />
              </PagerItem>
            </Pager>
          )}
        </GridCell>
        <GridCell
          colSpan={{ default: 12, md: 3 }}
          extraClasses={styles.pageSizeColumn}
        >
          {showPageSizeSelect && (
            <Select
              id="calculation-page-size"
              aria-label="Rows per page"
              inlineLabel="Rows"
              extraClasses={styles.pageSizeSelect}
              value={String(pagination.pageSize)}
              onChange={handlePageSizeChange}
            >
              {pagination.pageSizeOptions.map((size) => (
                <SelectOption key={size} value={String(size)}>
                  {size}
                </SelectOption>
              ))}
            </Select>
          )}
        </GridCell>
      </Grid>
    </div>
  );
};

export default React.memo(CalculationInputListPaginationControls);
